import { useLocale } from './LocaleProvider';
import { useTranslation } from './useTranslation';

/**
 * Locale-aware relative-time formatting.
 *
 * Returns a `rel(when)` function that converts a `Date | string | number` into a localized
 * relative-time string ("2 days ago", "il y a 3 semaines", "2일 전") using the browser's
 * `Intl.RelativeTimeFormat` keyed on the active locale.
 *
 * If `Intl.RelativeTimeFormat` is unavailable, falls back to the catalog keys under `time.*`.
 */
export function useRelativeTime() {
    const { locale } = useLocale();
    const t = useTranslation();

    const formatter = createFormatter(locale);

    return function rel(when: Date | string | number): string {
        const target =
            typeof when === 'number'
                ? new Date(when)
                : when instanceof Date
                  ? when
                  : new Date(when);
        if (Number.isNaN(target.getTime())) return '';
        const deltaMs = target.getTime() - Date.now();
        return formatter ? formatter(deltaMs) : fallbackFormat(deltaMs, t);
    };
}

type IntlFormatter = (deltaMs: number) => string;

function createFormatter(locale: string): IntlFormatter | null {
    if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') return null;
    const rtf = new Intl.RelativeTimeFormat(intlLocaleTag(locale), { numeric: 'auto' });

    return function format(deltaMs: number): string {
        const abs = Math.abs(deltaMs);
        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;
        const week = 7 * day;
        const month = 30 * day;
        const year = 365 * day;

        let unit: Intl.RelativeTimeFormatUnit;
        let value: number;

        if (abs < minute) {
            unit = 'second';
            value = Math.round(deltaMs / 1000);
        } else if (abs < hour) {
            unit = 'minute';
            value = Math.round(deltaMs / minute);
        } else if (abs < day) {
            unit = 'hour';
            value = Math.round(deltaMs / hour);
        } else if (abs < week) {
            unit = 'day';
            value = Math.round(deltaMs / day);
        } else if (abs < month) {
            unit = 'week';
            value = Math.round(deltaMs / week);
        } else if (abs < year) {
            unit = 'month';
            value = Math.round(deltaMs / month);
        } else {
            unit = 'year';
            value = Math.round(deltaMs / year);
        }

        return rtf.format(value, unit);
    };
}

// Manifest codes are platform-internal (e.g. "pt-br", "ko-kr"). Intl expects BCP47 tags with the
// language subtag lowercase and the region subtag uppercase ("pt-BR", "ko-KR").
function intlLocaleTag(code: string): string {
    const [lang, region] = code.split('-');
    return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase();
}

function fallbackFormat(
    deltaMs: number,
    t: (key: string, vars?: Record<string, string | number>) => string,
): string {
    const abs = Math.abs(deltaMs);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;

    if (abs < minute) return t('time.now');
    if (abs < hour) return t('time.minutesAgo', { n: Math.round(abs / minute) });
    if (abs < day) return t('time.hoursAgo', { n: Math.round(abs / hour) });
    if (abs < week) return t('time.daysAgo', { n: Math.round(abs / day) });
    if (abs < month) return t('time.weeksAgo', { n: Math.round(abs / week) });
    if (abs < year) return t('time.monthsAgo', { n: Math.round(abs / month) });
    return t('time.yearsAgo', { n: Math.round(abs / year) });
}
